import { createSlice } from '@reduxjs/toolkit';

import type { JsonFailure } from '@skynet/data-ws-core';
import { isOkCommand, parseResponse, registerErrorMessage } from '@skynet/data-ws-core';
import { actionOnMessage } from '@skynet/data-ws-mm';

import type { LoanAmountsState, JsonLoanAmountsSuccess } from './types';
import { LoanAmountsCommands } from './types';

import { addLoanAmount, updateLoanAmount, deleteLoanAmount } from './actions';

const allowedCommands = Object.values(LoanAmountsCommands);

// `parseResponse`'s error branch only fires for commands registered here — without
// these, the server's `error` ack for add/update/delete is silently dropped and
// `status`/`updateStatus`/`deleteStatus` can never reach 'error' (SKY-4616 review
// fix: the update/delete error toasts were wired in the UI but unreachable because
// nothing registered these commands outside of slice.test.ts's own setup).
const DEFAULT_ERROR_DESCRIPTION = 'Sorry, an unexpected error has occurred';

registerErrorMessage(LoanAmountsCommands.AddLoanAmount, {
  title: 'Add Loan Amount Error',
  getDescription: (json: JsonFailure) => json.message ?? DEFAULT_ERROR_DESCRIPTION,
});
registerErrorMessage(LoanAmountsCommands.UpdateLoanAmount, {
  title: 'Update Loan Amount Error',
  getDescription: (json: JsonFailure) => json.message ?? DEFAULT_ERROR_DESCRIPTION,
});
registerErrorMessage(LoanAmountsCommands.DeleteLoanAmount, {
  title: 'Delete Loan Amount Error',
  getDescription: (json: JsonFailure) => json.message ?? DEFAULT_ERROR_DESCRIPTION,
});

export const initialState: LoanAmountsState = {
  amounts: [],
  status: null,
  updateStatus: null,
  deleteStatus: null,
  error: null,
  updateError: null,
  deleteError: null,
};

const loanAmountsSlice = createSlice({
  name: 'loanAmounts',
  initialState,
  reducers: {
    resetAmountsStatus(state) {
      state.status = null;
      state.error = null;
    },
    resetUpdateAmountStatus(state) {
      state.updateStatus = null;
      state.updateError = null;
    },
    resetDeleteAmountStatus(state) {
      state.deleteStatus = null;
      state.deleteError = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(addLoanAmount, (state) => {
      state.status = 'pending';
    });

    builder.addCase(updateLoanAmount, (state) => {
      state.updateStatus = 'pending';
    });

    builder.addCase(deleteLoanAmount, (state) => {
      state.deleteStatus = 'pending';
    });

    builder.addCase(actionOnMessage, (state, action) => {
      const { json, skipProcessing, error, errorCommand } = parseResponse(
        action.payload,
        allowedCommands,
      );

      if (skipProcessing || !json) return;

      const { command, okCommand } = json;

      if (error) {
        if (errorCommand === LoanAmountsCommands.AddLoanAmount) {
          state.error = error;
          state.status = 'error';
        } else if (errorCommand === LoanAmountsCommands.UpdateLoanAmount) {
          state.updateError = error;
          state.updateStatus = 'error';
        } else if (errorCommand === LoanAmountsCommands.DeleteLoanAmount) {
          state.deleteError = error;
          state.deleteStatus = 'error';
        }
        return;
      }

      if (command === LoanAmountsCommands.LoanAmounts) {
        const { amounts } = json as JsonLoanAmountsSuccess;
        state.amounts = amounts;
        return;
      }

      if (isOkCommand(command)) {
        if (okCommand === LoanAmountsCommands.AddLoanAmount) {
          state.status = 'success';
        } else if (okCommand === LoanAmountsCommands.UpdateLoanAmount) {
          state.updateStatus = 'success';
        } else if (okCommand === LoanAmountsCommands.DeleteLoanAmount) {
          state.deleteStatus = 'success';
        }
      }
    });
  },
});

export const loanAmountsActions = {
  ...loanAmountsSlice.actions,
  addLoanAmount,
  updateLoanAmount,
  deleteLoanAmount,
};

export default loanAmountsSlice.reducer;
